import FleetTitle from '@/libs/antd/components/Title';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { ArrowRightOutlined } from '@ant-design/icons';
import { Button, Col, DatePicker, Divider, Form, Row, Select, Table } from 'antd';
import Input from 'antd/es/input/Input';
import { FC, useState } from 'react';
import dayjs from 'dayjs';
import { useApp } from '@/App';
import { useModals } from '@/components/modals/ModalProvider';
import {
	useQcPdSearch,
	useQcPdSearchOptions,
} from '@/libs/fleet-api/services/qc-pra-disbursement';
import type { QcPdSearchRequest } from '@/libs/fleet-api/services/qc-pra-disbursement-types';
import { useForm } from '@/libs/antd/form';
import RoboticStatusBadge from '@/pages/robotic/components/RoboticStatusBadge';

const QCPraPencairanListPage: FC = () => {
	const { navigate } = useApp();
	const { openCustomerDetail, openPurchaseOrderDetail } = useModals();

	const [searchParam, setSearchParam] = useState<QcPdSearchRequest>({
		pagination: {
			pageNumber: 1,
			pageSize: 10,
		},
	});

	const searchQuery = useQcPdSearch(searchParam);
	const optionsQuery = useQcPdSearchOptions();
	const isLoading = searchQuery.isFetching;

	const dataSource = (searchQuery.data?.data?.items ?? []).map((item, index) => ({
		...item,
		key: index + 1,
	}));

	const statusOptions = (optionsQuery.data?.data?.statusOptions ?? []).map((o) => ({
		label: o.value ?? '',
		value: o.id ?? '',
	}));

	const searchByOptions = (optionsQuery.data?.data?.searchByOptions ?? []).map((o) => ({
		label: o.value ?? '',
		value: o.id ?? '',
	}));

	const { form, fieldName } = useForm<{
		dateFrom?: dayjs.Dayjs | null;
		dateTo?: dayjs.Dayjs | null;
		status?: string;
		searchBy?: string;
		keyword?: string;
	}>();

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
			<FleetTitle>Quality Control Pra Pencairan</FleetTitle>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				labelCol={{ flex: 'none', style: { width: 150 } }}
				style={{ overflow: 'visible' }}
				onFinish={(values) => {
					setSearchParam({
						pagination: { pageNumber: 1, pageSize: 10 },
						dateFrom: values.dateFrom?.format('YYYY-MM-DD') ?? undefined,
						dateTo: values.dateTo?.format('YYYY-MM-DD') ?? undefined,
						status: values.status || undefined,
						search:
							values.keyword && values.searchBy
								? { by: values.searchBy, value: values.keyword }
								: undefined,
					});
				}}
			>
				<Row gutter={20}>
					<Col flex="none">
						<Form.Item label="Tanggal Activity">
							<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
								<Form.Item name={fieldName('dateFrom')} noStyle>
									<DatePicker format="DD/MM/YYYY" disabled={isLoading} />
								</Form.Item>
								<span>sd</span>
								<Form.Item name={fieldName('dateTo')} noStyle>
									<DatePicker format="DD/MM/YYYY" disabled={isLoading} />
								</Form.Item>
							</div>
						</Form.Item>
					</Col>
				</Row>
				<Divider style={{ margin: '20px 0' }} />
				<Row gutter={20}>
					<Col span={24}>
						<Form.Item
							name={fieldName('status')}
							initialValue="all"
							label="Status"
						>
							<Select
								options={statusOptions}
								style={{ width: 160 }}
								disabled={isLoading}
								loading={optionsQuery.isLoading}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider style={{ margin: '20px 0' }} />
				<Row gutter={20}>
					<Col flex="none">
						<Form.Item
							name={fieldName('searchBy')}
							initialValue={searchByOptions[0]?.value}
							label="Kata Kunci"
						>
							<Select
								options={searchByOptions}
								style={{ width: 200 }}
								disabled={isLoading}
								loading={optionsQuery.isLoading}
							/>
						</Form.Item>
					</Col>
					<Col flex="auto">
						<Form.Item name={fieldName('keyword')}>
							<Input placeholder="Search" disabled={isLoading} />
						</Form.Item>
					</Col>
					<Col flex="none" style={{ width: 190 }}>
						<Button
							block
							type="primary"
							htmlType="submit"
							disabled={isLoading}
						>
							SEARCH
						</Button>
					</Col>
				</Row>
			</Form>

			<Divider style={{ margin: 0 }} />

			<Table
				pagination={{
					total: searchQuery.data?.data.totalCount,
					pageSize: searchParam.pagination?.pageSize ?? 10,
					current: searchParam.pagination?.pageNumber ?? 1,
					onChange: (pageNumber) => {
						setSearchParam((prev) => ({
							...prev,
							pagination: {
								pageNumber,
								pageSize: prev.pagination.pageSize,
							},
						}));
					},
				}}
				scroll={{ x: 'max-content' }}
				bordered
				loading={isLoading}
				dataSource={dataSource}
				columns={[
				{
					title: 'Action',
					align: 'center',
					fixed: 'right',
					width: 1,
					render: (_, record) => {
						if (!record.isActionAllowed) {
							return <span style={{ color: '#bababa' }}><ArrowRightOutlined /></span>;
						}
						return (
							<a
								onClick={(e) => {
									e.preventDefault();
									navigate('QCPraPencairanPage' as never, {
										purchaseOrderNo: record.purchaseOrderNo,
										mode: 'edit',
									} as never);
								}}
							>
								<ArrowRightOutlined />
							</a>
						);
					},
				},
					{
						title: 'Nama Customer',
						render: (_, record) => {
							if (!record.customerName) return '-';
							if (!record.customerId) return record.customerName;
							return (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openCustomerDetail(record.customerId!);
									}}
								>
									{record.customerName}
								</a>
							);
						},
					},
					{
						title: 'FRM',
						render: (_, record) => record.frmName || '-',
					},
					{
						title: 'FBDH',
						render: (_, record) => record.fbdhName || '-',
					},
					{
						title: 'No DKRPP',
						render: (_, record) => record.disbursementNo || '-',
					},
					{
						title: 'No PO',
						render: (_, record) => {
							if (!record.purchaseOrderNo) return '-';
							if (!record.mppId || !record.purchaseOrderId) {
								return record.purchaseOrderNo;
							}
							return (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openPurchaseOrderDetail({
											mppId: record.mppId!,
											purchaseOrderId: record.purchaseOrderId!,
										});
									}}
								>
									{record.purchaseOrderNo}
								</a>
							);
						},
					},
					{
						title: 'Jenis Fasilitas',
						render: (_, record) => record.facilityType || '-',
					},
					{
						title: 'Asset Description',
						render: (_, record) => record.assetDescription || '-',
					},
					{
						title: 'Last Modified',
						render: (_, record) =>
							record.lastModified
								? dayjs(record.lastModified).format('DD/MM/YYYY HH:mm')
								: '-',
					},
					{
						title: 'Detail',
						align: 'center',
						render: (_, record) => {
							if (!record.isViewAllowed) {
								return <span style={{ color: '#bababa' }}>View Detail</span>;
							}
							return (
								<a
									onClick={(e) => {
										e.preventDefault();
										navigate('QCPraPencairanPage' as never, {
											purchaseOrderNo: record.purchaseOrderNo,
											mode: 'view',
										} as never);
									}}
								>
									View Detail
								</a>
							);
						},
					},
					{
						title: 'Status',
						render: (_, record) =>
							record.status ? (
								<RoboticStatusBadge status={record.status as 'Pending' | 'Finish'} />
							) : (
								'-'
							),
					},
				]}
			/>
		</div>
	);
};

export default QCPraPencairanListPage;
